<template>
  <wa-page
    view-slug="icons-landing"
    :is-showing-v7-prerelease-notice="true"
  >
    <wa-page-header slot="header" />

    <main
      slot="content"
      class="app-content"
      role="main"
    >
      <div
        class="padding-top-2xl"
        style="background: var(--white)"
      >
        <div class="container">
          <icons-header
            :selected-tab="selectedTab"
            @tab-change="setSelectedTab"
          />
          <icons-features :features="shortTermFeatures" />
          <icons-features-long-term :features="longTermFeatures" />

          <icons-controls
            :selected-tab="selectedTab"
            @tab-change="setSelectedTab"
            @release-change="setRelease"
            @all-versions-selected="navigateToVersions"
          />
        </div>
      </div>

      <transition-group
        name="tab-transition"
        tag="div"
        class="container padding-top-4xl padding-bottom-6xl"
        enter-active-class="animate__animated animate__faster animate__fadeIn"
      >
        <wa-old-version-alert
          v-if="isOlderVersion"
          key="old-version-alert"
          subject="icons"
          class="margin-bottom-3xl"
        />

        <icons-packs
          v-if="selectedTab === 'packs'"
          key="packs"
          class="tab-content"
        />

        <icons-categories
          v-if="selectedTab === 'categories'"
          key="categories"
          :release="release"
          class="tab-content"
        />
      </transition-group>
    </main>

    <wa-page-footer slot="footer" />
  </wa-page>
</template>

<script>
  import IconsHeader from './components/IconsHeader'
  import IconsFeatures from './components/IconsFeatures'
  import IconsFeaturesLongTerm from './components/IconsFeaturesLongTerm'
  import IconsControls from './components/IconsControls'
  import IconsPacks from './components/IconsPacks'
  import IconsCategories from './components/IconsCategories'
  import IconsLandingData from './IconsLandingData.json'
  import v7SaleMixin from 'app/views/v7SaleMixin'

  export default {
    name: 'IconsLanding',

    components: {
      IconsHeader,
      IconsFeatures,
      IconsFeaturesLongTerm,
      IconsControls,
      IconsPacks,
      IconsCategories
    },

    mixins: [v7SaleMixin],

    provide() {
      return {
        selectedTab: this.selectedTab,
        setSelectedTab: this.setSelectedTab
      }
    },

    data() {
      return {
        featuresData: IconsLandingData.featuresData,
        selectedTab: this.$route.query.t || 'packs',
        // delay in milliseconds to allow tab transition animation to complete before scrolling
        transitionDelay: 100
      }
    },

    computed: {
      isOlderVersion() {
        return this.release.versionParts.major != 6 ? true : false
      },

      longTermFeatures() {
        return this.featuresData.filter((feature) => feature.longTerm)
      },

      release() {
        return this.$releases.active
      },

      shortTermFeatures() {
        return this.featuresData.filter((feature) => !feature.longTerm)
      }
    },

    methods: {
      setSelectedTab(tab) {
        // update the URL query parameter to reflect the selected tab
        this.$router
          .replace({
            query: { ...this.$route.query, t: tab }
          })
          .catch((error) => {
            // ignore NavigationCancelled errors as they're expected when updating the hash
            if (error.name !== 'NavigationCancelled') {
              console.warn('Unexpected navigation error:', error)
            }
          })

        this.selectedTab = tab

        // wait for the tab transition animation to complete before scrolling to the new tab
        setTimeout(() => {
          const element = document.getElementById(tab)
          if (element) {
            // only scroll if we're not already at the target position (within 10px threshold)
            const currentScroll = window.pageYOffset
            const targetScroll = element.offsetTop

            if (Math.abs(currentScroll - targetScroll) > 10) {
              try {
                window.scrollTo({
                  top: targetScroll,
                  behavior: 'smooth'
                })

                // update the URL hash after smooth scrolling begins
                window.location.hash = tab
              } catch (error) {
                console.warn('Failed to scroll to element:', error)
              }
            }
          }
        }, this.transitionDelay)
      },

      navigateToVersions() {
        this.$router.push({ name: 'versions' })
      },

      setRelease(release) {
        let name = 'latest.icons'

        if (release.versionParts.major === 5) {
          name = 'five.icons'
        }

        window.location.replace(
          this.$router.resolve({
            name,
            query: this.$route.query,
            hash: `#${this.selectedTab}`
          }).href
        )
      }
    }
  }
</script>
